import { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { PyrenzDropzone } from '@client/theme';
import { usePyrenzAlert } from '@renderer/provider';

interface DropzoneProps {
  onDrop: (acceptedFiles: File[]) => void;
  label?: string;
  className?: string;
  initialImage?: string | null;
}

export function Dropzone({
  onDrop,
  label = 'Click to upload image',
  className,
  initialImage,
}: DropzoneProps) {
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(
    initialImage || null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const showAlert = usePyrenzAlert();

  useEffect(() => {
    if (initialImage) setBannerImagePreview(initialImage);
  }, [initialImage]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showAlert('Please upload an image file.', 'Alert');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setBannerImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    onDrop([file]);

    // Reset input to allow re-uploading the same file
    event.target.value = '';
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <PyrenzDropzone className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {bannerImagePreview ? (
        <Box
          component="img"
          src={bannerImagePreview}
          alt="upload preview"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            objectFit: 'cover',
            borderRadius: '20px',
            cursor: 'pointer',
          }}
          onClick={triggerFileSelect}
        />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            py: 5,
            color: '#aaa',
            gap: 1,
            textAlign: 'center',
            cursor: 'pointer',
          }}
          onClick={triggerFileSelect}
        >
          {label}
        </Box>
      )}
    </PyrenzDropzone>
  );
}
